import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { Container, Row, Col, Card, Button, Alert, Spinner } from 'react-bootstrap';
import api from '../api/axios';
import QuestionDisplay from './QuestionDisplay';
import { startExam, answerQuestion, setCurrentIndex, resumeExam } from '../store/examSlice';

const ExamMode = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const group = location.state?.group || '';
  console.log('ExamMode group from location.state:', group);

  const { sessionId, questions, currentIndex, answers } = useSelector((state) => state.exam);
  const [selected, setSelected] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [submittingFinish, setSubmittingFinish] = useState(false);
  const [timerActive, setTimerActive] = useState(true);
  const [timeLeft, setTimeLeft] = useState(7200);

  // Restore selected answer for current question
  useEffect(() => {
    if (questions.length > 0) {
      const qid = questions[currentIndex]?.id;
      if (qid && answers[qid]) {
        setSelected(answers[qid]);
        setSubmitted(true);
      } else {
        setSelected('');
        setSubmitted(false);
      }
    }
  }, [currentIndex, questions, answers]);

  // Timer
  useEffect(() => {
    if (!timerActive) return;
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          handleTimeOut();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [timerActive]);

  const handleTimeOut = () => {
    alert('Time is up! Submitting exam...');
    finishExam();
  };

  const formatTime = (seconds) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };

  const getQuestionCount = async () => {
    try {
      const res = await api.get(`/question-counts.php?group=${group === 'All Groups' ? '' : group}`);
      return res.data.all;
    } catch (err) {
      console.error('Failed to get question count', err);
      return 150;
    }
  };

  // ---------------------------------------------------------------------
  // Check for incomplete session on mount (with group matching)
  // ---------------------------------------------------------------------
  useEffect(() => {
    const initializeExam = async () => {
      setLoading(true);
      try {
        const incompleteRes = await api.get('/exam.php?action=incomplete');
        const incompleteSessions = incompleteRes.data;

        let resumed = false;
        if (incompleteSessions.length > 0) {
          for (const sess of incompleteSessions) {
            // Only resume if the session's group matches the selected group
            if (sess.group_name === group) {
              const success = await resumeExistingSession(sess.id);
              if (success) {
                resumed = true;
                break;
              }
            }
          }
        }
        if (!resumed) {
          await startNewExam();
        }
      } catch (err) {
        console.error(err);
        alert('Failed to initialize exam: ' + (err.response?.data?.error || err.message));
        navigate('/dashboard');
      } finally {
        setLoading(false);
      }
    };

    if (!sessionId) {
      initializeExam();
    } else {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------------------------------------------------
  // Resume an existing session (with client-side reordering)
  // ---------------------------------------------------------------------
  const resumeExistingSession = async (sessId) => {
    try {
      const sessionRes = await api.get(`/exam.php?action=get&sessionId=${sessId}`);
      const session = sessionRes.data;

      const questionOrder = session.question_order || [];
      if (questionOrder.length === 0) {
        console.warn(`Session ${sessId} has no question order, skipping.`);
        return false;
      }

      const idsParam = questionOrder.join(',');
      const questionsRes = await api.get(`/questions.php?ids=${idsParam}`);
      const fetchedQuestions = questionsRes.data;

      const orderedQuestions = questionOrder.map(id =>
        fetchedQuestions.find(q => q.id === id)
      ).filter(q => q);

      console.log('Resumed question IDs (expected):', questionOrder);
      console.log('Resumed question IDs (received raw):', fetchedQuestions.map(q => q.id));
      console.log('Resumed question IDs (reordered):', orderedQuestions.map(q => q.id));

      dispatch(resumeExam({
        sessionId: sessId,
        questions: orderedQuestions,
        answers: session.answers || {},
        currentIndex: session.current_question_index || 0
      }));
      return true;
    } catch (err) {
      console.error(`Error resuming session ${sessId}:`, err);
      return false;
    }
  };

  // ---------------------------------------------------------------------
  // Start a brand new exam (with client-side reordering and detailed logs)
  // ---------------------------------------------------------------------
  const startNewExam = async () => {
    console.log('startNewExam called with group:', group);
    const totalQ = await getQuestionCount();
    console.log('totalQ from getQuestionCount:', totalQ);

    const startRes = await api.post('/exam.php?action=start', {
      mode: 'exam',
      totalQuestions: totalQ,
      group: group === 'All Groups' ? null : group,
    });
    console.log('startRes.data:', startRes.data);
    const newSessionId = startRes.data.sessionId;

    const sessionRes = await api.get(`/exam.php?action=get&sessionId=${newSessionId}`);
    const session = sessionRes.data;
    const questionOrder = session.question_order || [];
    console.log('questionOrder from session:', questionOrder);

    const idsParam = questionOrder.join(',');
    const questionsRes = await api.get(`/questions.php?ids=${idsParam}`);
    const fetchedQuestions = questionsRes.data;
    console.log('fetchedQuestions IDs:', fetchedQuestions.map(q => q.id));

    const orderedQuestions = questionOrder.map(id =>
      fetchedQuestions.find(q => q.id === id)
    ).filter(q => q);

    dispatch(startExam({
      sessionId: newSessionId,
      mode: 'exam',
      questions: orderedQuestions
    }));
  };

  // ---------------------------------------------------------------------
  // Answer handling
  // ---------------------------------------------------------------------
  const handleSelect = (opt) => {
    if (!submitted) setSelected(opt);
  };

  const handleSubmit = async () => {
    if (!selected) {
      alert('Please select an answer');
      return;
    }
    const questionId = questions[currentIndex].id;
    try {
      await api.post('/exam.php?action=answer', {
        sessionId,
        questionId,
        selectedOptions: selected,
        currentIndex: currentIndex + 1
      });
      dispatch(answerQuestion({ questionId, selected }));
      setSubmitted(true);
    } catch (err) {
      console.error(err);
      alert('Error saving answer');
    }
  };

  const handleNext = () => {
    if (!submitted) {
      alert('Please submit your answer first');
      return;
    }
    if (currentIndex + 1 < questions.length) {
      dispatch(setCurrentIndex(currentIndex + 1));
      setSubmitted(false);
    }
  };

  const finishExam = async () => {
    setSubmittingFinish(true);
    try {
      const totalTime = 7200 - timeLeft;
      const res = await api.post('/exam.php?action=complete', { 
        sessionId,
        totalTime 
      });
      navigate(`/exam/report/${res.data.sessionId}`);
    } catch (err) {
      console.error(err);
      alert('Failed to complete exam: ' + (err.response?.data?.error || err.message));
      setSubmittingFinish(false);
    }
  };

  const handleFinish = () => {
    if (!submitted && currentIndex === questions.length - 1) {
      alert('Please submit your answer first');
      return;
    }
    finishExam();
  };

  const handlePause = () => {
    setTimerActive(false);
    navigate('/dashboard');
  };

  const handleEnd = () => {
    if (window.confirm('Are you sure you want to end the exam? Your progress will be saved.')) {
      finishExam();
    }
  };

  if (loading) return <Container className="mt-5 text-center"><Spinner animation="border" /></Container>;
  if (!questions.length) return <Container className="mt-5"><Alert variant="warning">No questions loaded</Alert></Container>;

  const currentQuestion = questions[currentIndex];
  const isLastQuestion = currentIndex === questions.length - 1;

  return (
    <Container fluid className="p-4" style={{ backgroundColor: '#f8f9fa', minHeight: '100vh' }}>
      <Row className="mb-3 align-items-center">
        <Col>
          <h4 className="text-primary">NCLEX EXAM</h4>
        </Col>
        <Col className="text-end">
          <span style={{ fontWeight: 'bold', fontSize: '1.2rem' }}>
            {formatTime(timeLeft)}    Item {currentIndex + 1} of {questions.length}
          </span>
        </Col>
      </Row>

      <Card className="shadow-sm">
        <Card.Body>
          <Card.Text className="mt-3">{currentQuestion.question_text}</Card.Text>
          <QuestionDisplay
            question={currentQuestion}
            selectedOptions={selected}
            onSelect={handleSelect}
            showFeedback={false}
          />
          <div className="mt-3">
            <Button variant="primary" onClick={handleSubmit} disabled={submitted}>
              Submit
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Row className="mt-3">
        <Col>
          <Button variant="outline-secondary" className="me-2" onClick={handleEnd} disabled={submittingFinish}>
            End
          </Button>
          <Button variant="outline-secondary" onClick={handlePause} disabled={submittingFinish}>
            Pause
          </Button>
        </Col>
        <Col className="text-end">
          {isLastQuestion ? (
            <Button
              variant="primary"
              onClick={handleFinish}
              disabled={!submitted || submittingFinish}
            >
              {submittingFinish ? 'Finishing...' : 'Finish Exam'}
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={handleNext}
              disabled={!submitted}
            >
              Next →
            </Button>
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ExamMode;